/**
 * Recipe-book payloads come in three historical shapes:
 * - favorite:            ingredients = Ingredient[], steps = CookingStep[]
 * - manual:              ingredients = string[],     steps = string[]
 * - restaurant_capture:  ingredients = {name, quantity, unit}[], steps = CookingStep[]
 * This turns any of them into one displayable shape.
 */
export interface BookIngredient {
  name: string;
  quantity: string | null;
}

export interface BookStep {
  verb: string | null;
  text: string;
  quantity: string | null;
  timerSeconds: number | null;
}

export interface BookRecipe {
  title: string;
  summary: string | null;
  servings: number | null;
  prepMinutes: number | null;
  cookMinutes: number | null;
  ingredients: BookIngredient[];
  steps: BookStep[];
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function formatQuantity(quantity: unknown, unit: unknown): string | null {
  const q = num(quantity);
  const u = str(unit);
  if (q === null) return u;
  const n = Number.isInteger(q) ? String(q) : String(Math.round(q * 100) / 100);
  return u ? `${n} ${u}` : n;
}

function toIngredient(raw: unknown): BookIngredient | null {
  if (typeof raw === "string") {
    return raw.trim() ? { name: raw.trim(), quantity: null } : null;
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const name = str(o.name);
    if (!name) return null;
    return { name, quantity: formatQuantity(o.quantity, o.unit) };
  }
  return null;
}

function toStep(raw: unknown): BookStep | null {
  if (typeof raw === "string") {
    return raw.trim()
      ? { verb: null, text: raw.trim(), quantity: null, timerSeconds: null }
      : null;
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const verb = str(o.verb);
    const target = str(o.target);
    if (!verb && !target) return null;
    return {
      verb,
      text: target ?? "",
      quantity: str(o.quantity),
      timerSeconds: num(o.timer_seconds),
    };
  }
  return null;
}

function list<T>(v: unknown, map: (x: unknown) => T | null): T[] {
  if (!Array.isArray(v)) return [];
  return v.map(map).filter((x): x is T => x !== null);
}

export function normalizeBookPayload(
  payload: Record<string, unknown>,
  fallbackTitle: string,
): BookRecipe {
  return {
    title: str(payload.title) ?? fallbackTitle,
    summary: str(payload.summary),
    servings: num(payload.servings),
    prepMinutes: num(payload.prep_minutes),
    cookMinutes: num(payload.cook_minutes),
    ingredients: list(payload.ingredients, toIngredient),
    steps: list(payload.steps ?? payload.prep_steps, toStep),
  };
}
